// src/components/PlayerProfileModal.jsx

import React, { useState, useEffect } from 'react';
import { useAuthCliente } from '../context/AuthClienteContext';
import { db } from '../firebase/firebase-config';
import {
  doc, getDoc, collection, query, where, orderBy, onSnapshot // ✅ Importamos onSnapshot
} from 'firebase/firestore';
import {
  Modal, ModalOverlay, ModalContent, ModalFooter, ModalBody, ModalCloseButton, Button,
  VStack, Text, Box, Spinner, Center, Heading, Divider, Avatar, HStack,
  useDisclosure, IconButton, Tag, Tabs, TabList, TabPanels, Tab, TabPanel
} from '@chakra-ui/react';
import { FaInfoCircle, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

// --- Componente MatchResultCard (sin cambios) ---
const MatchResultCard = ({ match }) => {
  const winnerUid = match.winner_uid;
  const dateStr = match.approvedAt?.toDate().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) || 'N/A';
  const typeTagScheme = match.type === 'tournament' ? 'purple' : 'orange';
  return ( <Box p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200"> <VStack spacing={3}> <Text fontSize="sm" color="gray.500">{dateStr}</Text> <VStack w="full" spacing={0}> <HStack w="full" justify="space-between" p={2} bg={winnerUid === match.player1_uid ? 'green.50' : 'red.50'}> <HStack> <Text fontWeight="bold" color={winnerUid === match.player1_uid ? 'green.600' : 'inherit'}> {match.player1_name} </Text> <Tag size="sm" colorScheme={winnerUid === match.player1_uid ? 'green' : 'red'}> {winnerUid === match.player1_uid ? 'Ganó' : 'Perdió'} </Tag> </HStack> <HStack> {match.scores.map((s, idx) => <Text key={idx} w="25px" textAlign="center" fontWeight="bold">{s.player1_score}</Text>)} </HStack> </HStack> <HStack w="full" justify="space-between" p={2} bg={winnerUid === match.player2_uid ? 'green.50' : 'red.50'}> <HStack> <Text fontWeight="bold" color={winnerUid === match.player2_uid ? 'green.600' : 'inherit'}> {match.player2_name} </Text> <Tag size="sm" colorScheme={winnerUid === match.player2_uid ? 'green' : 'red'}> {winnerUid === match.player2_uid ? 'Ganó' : 'Perdió'} </Tag> </HStack> <HStack> {match.scores.map((s, idx) => <Text key={idx} w="25px" textAlign="center" fontWeight="bold">{s.player2_score}</Text>)} </HStack> </HStack> </VStack> <HStack justify="center" spacing={2}> {match.roundName && <Tag colorScheme="red" size="sm">{match.roundName}</Tag>} <Tag colorScheme={typeTagScheme} size="sm">{match.type === 'tournament' ? 'Torneo' : 'Reta'}</Tag> </HStack> </VStack> </Box> );
};

export default function PlayerProfileModal({ isOpen, onClose, member }) {
  const { currentUser } = useAuthCliente();
  const [tournamentMatches, setTournamentMatches] = useState([]);
  const [retaMatches, setRetaMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [tournamentPage, setTournamentPage] = useState(1);
  const [retaPage, setRetaPage] = useState(1);
  const itemsPerPage = 3;
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const { isOpen: isBioOpen, onOpen: onBioOpen, onClose: onBioClose } = useDisclosure();

  // EFECTO: cargar preguntas de perfil (sin cambios)
  useEffect(() => { if (!isOpen || !member || !currentUser?.companyId) return; setLoadingQuestions(true); (async () => { const compRef = doc(db, 'companies', currentUser.companyId); const snap = await getDoc(compRef); setQuestions(snap.exists() ? snap.data().profileQuestions || [] : []); setLoadingQuestions(false); })(); }, [isOpen, member, currentUser]);

  // ✅ --- LÓGICA DE FETCHING REESCRITA CON onSnapshot ---
  useEffect(() => {
    if (!isOpen || !member || !currentUser?.companyId) {
      setLoadingMatches(false);
      return;
    }
    setLoadingMatches(true);

    const ref = collection(db, 'companies', currentUser.companyId, 'match_results');
    
    // Función para procesar y combinar los resultados de las consultas
    const processSnapshots = (snap1, snap2, matchType) => {
        const combined = new Map();
        snap1.docs.forEach(d => combined.set(d.id, { id: d.id, ...d.data() }));
        snap2.docs.forEach(d => combined.set(d.id, { id: d.id, ...d.data() }));
        
        const sortedMatches = Array.from(combined.values()).sort((a, b) => b.approvedAt.toDate() - a.approvedAt.toDate());
        
        if (matchType === 'tournament') {
            setTournamentMatches(sortedMatches);
        } else {
            setRetaMatches(sortedMatches);
        }
    };

    // Consultas para Torneos
    const qTournament1 = query(ref, where('status', '==', 'approved'), where('player1_uid', '==', member.id), where('type', '==', 'tournament'));
    const qTournament2 = query(ref, where('status', '==', 'approved'), where('player2_uid', '==', member.id), where('type', '==', 'tournament'));
    
    // Consultas para Retas
    const qReta1 = query(ref, where('status', '==', 'approved'), where('player1_uid', '==', member.id), where('type', '==', 'reta'));
    const qReta2 = query(ref, where('status', '==', 'approved'), where('player2_uid', '==', member.id), where('type', '==', 'reta'));

    // Variables para almacenar los resultados de cada snapshot
    let tournamentSnap1Data = { docs: [] }, tournamentSnap2Data = { docs: [] };
    let retaSnap1Data = { docs: [] }, retaSnap2Data = { docs: [] };

    const unsubTournament1 = onSnapshot(qTournament1, (snap) => {
        tournamentSnap1Data = snap;
        processSnapshots(tournamentSnap1Data, tournamentSnap2Data, 'tournament');
        setLoadingMatches(false);
    });
    const unsubTournament2 = onSnapshot(qTournament2, (snap) => {
        tournamentSnap2Data = snap;
        processSnapshots(tournamentSnap1Data, tournamentSnap2Data, 'tournament');
        setLoadingMatches(false);
    });
    const unsubReta1 = onSnapshot(qReta1, (snap) => {
        retaSnap1Data = snap;
        processSnapshots(retaSnap1Data, retaSnap2Data, 'reta');
        setLoadingMatches(false);
    });
    const unsubReta2 = onSnapshot(qReta2, (snap) => {
        retaSnap2Data = snap;
        processSnapshots(retaSnap1Data, retaSnap2Data, 'reta');
        setLoadingMatches(false);
    });

    // Función de limpieza para cancelar las suscripciones al cerrar el modal
    return () => {
        unsubTournament1();
        unsubTournament2();
        unsubReta1();
        unsubReta2();
    };
  }, [isOpen, member, currentUser]);

  if (!member) return null;

  const totalTournamentPages = Math.ceil(tournamentMatches.length / itemsPerPage);
  const paginatedTournamentMatches = tournamentMatches.slice((tournamentPage - 1) * itemsPerPage, tournamentPage * itemsPerPage);
  const totalRetaPages = Math.ceil(retaMatches.length / itemsPerPage);
  const paginatedRetaMatches = retaMatches.slice((retaPage - 1) * itemsPerPage, retaPage * itemsPerPage);
  const birth = member.birthDate ? new Date(member.birthDate) : null; const age = birth ? Math.floor((Date.now() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A'; const sexoLabel = member.sexo === 'fem' || member.sexo === 'femenino' ? '♀ Femenino' : member.sexo === 'mas' || member.sexo === 'masculino' ? '♂ Masculino' : ''; const categoryLabel = member.category || 'N/A';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <Box bg="green.800" p={6} textAlign="center">
            <Avatar size="2xl" name={member.name} src={member.photoURL} mb={4} />
            <Heading size="lg" color="white">{member.name}</Heading>
            <HStack spacing={3} justify="center" mt={2}><Tag size="md" colorScheme={member.sexo?.startsWith('f') ? 'pink' : 'blue'}>{sexoLabel}</Tag><Text color="whiteAlpha.800">Edad: {age}</Text><Text color="whiteAlpha.800">| Categoría: {categoryLabel}</Text>{currentUser.uid === member.id && <Tag colorScheme="red">Tú</Tag>}</HStack>
            <HStack spacing={8} justify="center" mt={4} mb={4}><VStack spacing={1}><Text fontSize="sm" color="whiteAlpha.800">Puntos Torneo</Text><Text fontSize="2xl" color="white" fontWeight="bold">{member.points ?? 0}</Text></VStack><VStack spacing={1}><Text fontSize="sm" color="whiteAlpha.800">Puntos Retas</Text><Text fontSize="2xl" color="white" fontWeight="bold">{member.retasPoints ?? 0}</Text></VStack></HStack>
            <Button size="sm" leftIcon={<FaInfoCircle />} onClick={onBioOpen} colorScheme="purple">BIO</Button>
          </Box>
          <ModalBody>
            <Heading size="md" mb={4}>Últimos Partidos</Heading>
            <Tabs isFitted variant="soft-rounded" colorScheme="green">
              <TabList mb="1em">
                <Tab>Torneos ({tournamentMatches.length})</Tab>
                <Tab>Retas ({retaMatches.length})</Tab>
              </TabList>
              <TabPanels>
                <TabPanel p={0}>
                  {loadingMatches ? <Center h="150px"><Spinner /></Center> :
                    paginatedTournamentMatches.length > 0 ? (
                      <VStack spacing={4} align="stretch">
                        {paginatedTournamentMatches.map(match => <MatchResultCard key={match.id} match={match} />)}
                        {totalTournamentPages > 1 && (
                            <HStack justify="center" spacing={4} mt={2}>
                                <IconButton icon={<FaArrowLeft />} isDisabled={tournamentPage === 1} onClick={() => setTournamentPage(p => p - 1)} aria-label="Anterior"/>
                                <Text>Página {tournamentPage} de {totalTournamentPages}</Text>
                                <IconButton icon={<FaArrowRight />} isDisabled={tournamentPage === totalTournamentPages} onClick={() => setTournamentPage(p => p + 1)} aria-label="Siguiente"/>
                            </HStack>
                        )}
                      </VStack>
                    ) : ( <Center p={4} bg="gray.50" borderRadius="md"><Text color="gray.500">Este jugador no tiene partidos de torneo registrados.</Text></Center> )
                  }
                </TabPanel>
                <TabPanel p={0}>
                 {loadingMatches ? <Center h="150px"><Spinner /></Center> :
                    paginatedRetaMatches.length > 0 ? (
                      <VStack spacing={4} align="stretch">
                        {paginatedRetaMatches.map(match => <MatchResultCard key={match.id} match={match} />)}
                        {totalRetaPages > 1 && (
                            <HStack justify="center" spacing={4} mt={2}>
                                <IconButton icon={<FaArrowLeft />} isDisabled={retaPage === 1} onClick={() => setRetaPage(p => p - 1)} aria-label="Anterior"/>
                                <Text>Página {retaPage} de {totalRetaPages}</Text>
                                <IconButton icon={<FaArrowRight />} isDisabled={retaPage === totalRetaPages} onClick={() => setRetaPage(p => p + 1)} aria-label="Siguiente"/>
                            </HStack>
                        )}
                      </VStack>
                    ) : ( <Center p={4} bg="gray.50" borderRadius="md"><Text color="gray.500">Este jugador no tiene partidos de retas registrados.</Text></Center> )
                  }
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBioOpen} onClose={onBioClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={6}><Heading size="md" mb={4}>Biografía</Heading><VStack spacing={2} align="start" mb={4}>{sexoLabel && (<Tag size="sm" colorScheme={member.sexo?.startsWith('f') ? 'pink' : 'blue'}>{sexoLabel}</Tag>)}<Text>Edad: {age}</Text><Text>Categoría: {categoryLabel}</Text><Divider /></VStack>{loadingQuestions ? (<Center><Spinner /></Center>) : questions.length ? (<VStack spacing={4} align="stretch">{questions.map(q => (<Box key={q.id}><Text fontWeight="bold">{q.text}</Text><Text color="gray.600" pl={2}>{member.profileAnswers?.[q.id] || 'Sin respuesta'}</Text></Box>))}</VStack>) : (<Text>Este club no ha configurado biografía para este jugador.</Text>)}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}